import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FormProvider } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import SmsForm from '@features/ChannelsForms/SmsForm';
import WhatsAppForm from '@features/ChannelsForms/WhatsAppForm';
import SelectChannel from '@features/ChannelsForms/SelectChannel';
import WhatsAppStatus from '@features/ChannelsForms/WhatsAppStatus';
import WhatsAppCheck from '@features/ChannelsForms/WhatsAppCheck';
import LiveChatForm from '@features/ChannelsForms/LiveChatForm';
import LiveChatCode from '@features/ChannelsForms/LiveChatHTMLCode';
import FacebookForm from '@features/ChannelsForms/FacebookForm';
import EmailForm from '@features/ChannelsForms/EmailForm';
import WhatsAppBusinessForm from '@features/ChannelsForms/WhatsAppBusinessForm';
import { Typography } from '@components/Typography';
import { useTranslation, useEditChannelsForm } from '@hooks';
import { channelsActions, alertActions, accountActions } from '@actions';
import { channelsService, hubService } from '@services';
import { WsHubMessage } from '@constants/ws-hub';
import { CHANNEL, STATUS, SmsProviderType } from '@constants/channels';
import { handleApiError } from '@helpers/api';
import { convertWorkingHours } from '@helpers/dates';
import { preparePhoneNumberForSubmit } from '@helpers/phone';
import { Modal } from './styles';

const CreateChannel = ({ modal, setModal, channel, setChannel, accountLocale }) => {
    const { t } = useTranslation();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const twillioUid = useRef(uuidv4());

    const { getValues, setValue, control, reset, watch, trigger, isValid } = useEditChannelsForm(modal.type);

    useEffect(() => {
        if (channel?.id) {
            hubService.subscribeToEvents({
                [WsHubMessage.QrCode(channel?.id)]: qrCode => {
                    setModal(prev => ({ ...prev, qrCode: qrCode }));
                },
                [WsHubMessage.ChannelStatus(channel?.id)]: status => {
                    if (status === 'Ready') {
                        setModal(prev => ({ ...prev, step: 4, status: STATUS.SUCCESS }));
                    } else if (status === 'Disabled') {
                        setModal(prev => ({ ...prev, step: 4, status: STATUS.NUMBER_EXISTS }));
                    } else if (status === 'Pending') {
                        setModal(prev => ({ ...prev, step: 3, status: STATUS.PENDING }));
                    }
                },
            });
        }
        return () => {
            if (channel?.id) {
                hubService.unsubscribeFromEvents([
                    WsHubMessage.ChannelStatus(channel?.id),
                    WsHubMessage.QrCode(channel?.id),
                ]);
            }
        };
    }, [channel]);

    const channelMapCb = {
        [CHANNEL.SMS]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelSms({
                    ...(values?.provider === SmsProviderType.Twilio && { id: twillioUid.current }),
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    smsParameters: {
                        senderId: values?.senderId,
                        provider: values?.provider,
                        accountId: values?.accountId,
                        apiKey: values?.apiKey,
                        token: values?.token,
                        phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber),
                        login: values?.login,
                        password: values?.password,
                    },
                });
            },
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.WHATSAPP]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelWhatsApp({
                    ...values,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                });
            },
            nextStep: () => setModal(prev => ({ ...prev, step: 3 })),
        },
        [CHANNEL.WHATSAPP_BUSINESS]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelWhatsAppBusiness({
                    ...values,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                });
            },
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.LIVE_CHAT]: {
            apiCall: values => {
                if (!values?.contactEmailRequired && !values?.contactPhoneRequired) {
                    setIsSubmitting(false);
                    const error = new Error('error');
                    error.errorCode = 'phoneOrEmailRequired';
                    return Promise.reject(error);
                } else if (
                    values?.notifyAboutMissingConversations &&
                    !values?.smsChannelId &&
                    !values?.emailChannelId
                ) {
                    setIsSubmitting(false);
                    const error = new Error('error');
                    error.errorCode = 'notificationChannelRequired';
                    return Promise.reject(error);
                }
                return channelsService.createOrUpdateChannelLiveChat({
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    labels: values.labels,
                    liveChatParameters: {
                        locations: values.locationsEnabled ? values.locations.map(item => item.id) : [],
                        smsChannelId: values.smsChannelId,
                        emailChannelId: values.emailChannelId === 'PLUSPOINT_EMAIL' ? null : values.emailChannelId,
                        useDefaultEmailChannel: values.emailChannelId === 'PLUSPOINT_EMAIL',
                        notifyAboutMissingConversations: values.notifyAboutMissingConversations,
                        welcomeHeading: values.welcomeHeading,
                        welcomeTagline: values.welcomeTagline,
                        workingHoursEnabled: values.workingHoursEnabled,
                        offlineMessage: values.offlineMessage,
                        workingHours: values.workingHours
                            ? { days: convertWorkingHours(values.workingHours.days, 'toUTC') }
                            : values.workingHours,
                        fields: {
                            Email: {
                                type: 0,
                                enable: values.contactEmail,
                                required: values.contactEmailRequired,
                            },
                            Phone: {
                                type: 1,
                                enable: values.contactPhone,
                                required: values.contactPhoneRequired,
                            },
                            Name: {
                                type: 2,
                                enable: values.contactName,
                                required: values.contactNameRequired,
                            },
                        },
                    },
                });
            },
            nextStep: newChannel => setModal(prev => ({ ...prev, step: 3, liveChatPublicId: newChannel.publicId })),
        },
        [CHANNEL.FACEBOOK]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelFacebook({
                    ...values,
                    saveInstagramReplies: undefined,
                });
            },
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.INSTAGRAM]: {
            apiCall: values => channelsService.createOrUpdateChannelInstagram(values),
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.EMAIL]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelEmail({
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    emailParameters: {
                        provider: values.provider,
                        smtpHost: values.smtpHost,
                        smtpPort: values.smtpPort,
                        username: values.username,
                        isSSL: values.isSSL,
                        email: values.email,
                        password: values.password,
                    },
                });
            },
            nextStep: () => onCloseModal(),
        },
    };

    const onClickSubmit = async () => {
        switch (true) {
            case modal.step === 1:
                setModal(prev => ({ ...prev, step: 2 }));
                break;
            case modal.step === 2:
                setIsSubmitting(true);
                try {
                    const values = getValues();
                    const newChannel = await channelMapCb[modal.type].apiCall(values);
                    if (modal.type === CHANNEL.WHATSAPP) {
                        setChannel(newChannel);
                    }
                    await dispatch(channelsActions.get());
                    await dispatch(accountActions.get());
                    channelMapCb[modal.type].nextStep(newChannel);

                    dispatch(alertActions.success(t('alertMessages.createSuccess')));
                } catch (error) {
                    handleApiError(error);
                } finally {
                    setIsSubmitting(false);
                }
                break;
            case modal.step === 3 && modal.type === CHANNEL.LIVE_CHAT:
                onCloseModal();
                break;
            case modal.step === 4 && modal.type === CHANNEL.WHATSAPP:
                try {
                    await dispatch(channelsActions.get());
                    await dispatch(accountActions.get());
                    onCloseModal();
                } catch (error) {
                    handleApiError(error);
                }
                break;
            default:
                break;
        }
    };

    const onClickBack = () => {
        setModal(prev => ({ ...prev, step: prev.step - 1 }));
        reset();
    };

    const onCloseModal = async () => {
        setModal({
            open: false,
            step: 1,
            type: CHANNEL.SMS,
            status: STATUS.PENDING,
            qrCode: '',
            liveChatPublicId: null,
        });
        setChannel({});
    };

    const getActionsText = () => {
        switch (true) {
            case modal.step === 1:
            case modal.step === 2 && modal.type === CHANNEL.WHATSAPP:
            case modal.step === 2 && modal.type === CHANNEL.LIVE_CHAT:
                return { primaryText: t('buttons.continue'), secondaryText: t('buttons.cancel') };
            case modal.step === 2 && modal.type === CHANNEL.SMS:
            case modal.step === 2 && modal.type === CHANNEL.FACEBOOK:
            case modal.step === 2 && modal.type === CHANNEL.INSTAGRAM:
            case modal.step === 2 && modal.type === CHANNEL.EMAIL:
            case modal.step === 2 && modal.type === CHANNEL.WHATSAPP_BUSINESS:
            case modal.step === 3 && modal.type === CHANNEL.LIVE_CHAT:
                return { primaryText: t('buttons.done'), secondaryText: t('buttons.cancel') };
            case modal.step === 3 && modal.type === CHANNEL.WHATSAPP:
                return { primaryText: '', secondaryText: t('buttons.cancel') };
            case modal.step === 4 && modal.type === CHANNEL.WHATSAPP && modal.status === STATUS.SUCCESS:
                return { primaryText: t('buttons.done'), secondaryText: '' };
            case modal.step === 4 && modal.type === CHANNEL.WHATSAPP && modal.status === STATUS.NUMBER_EXISTS:
                return { primaryText: '', secondaryText: t('buttons.close') };
            default:
                return { primaryText: '', secondaryText: '' };
        }
    };

    const getChannelText = () => {
        switch (modal.type) {
            case CHANNEL.SMS:
                return t('common.sms');
            case CHANNEL.WHATSAPP:
            case CHANNEL.WHATSAPP_BUSINESS:
                return t('common.whatsApp');
            case CHANNEL.LIVE_CHAT:
                return t('common.liveChatLowercase');
            case CHANNEL.FACEBOOK:
                return t('common.facebookMessenger');
            case CHANNEL.INSTAGRAM:
                return t('common.instagram');
            case CHANNEL.EMAIL:
                return t('common.email');
            default:
                return '';
        }
    };

    const getTitleText = () => {
        if (modal.step === 1) {
            return { title: t('SettingsChannels.firstStepTitle'), subtitle: t('SettingsChannels.firstStepSubtitle') };
        } else if (modal.step > 1) {
            return {
                title: t('SettingsChannels.createChannelTitle', { channelType: getChannelText() }),
                subtitle: t('SettingsChannels.editChannelSubtitle', { channelType: getChannelText() }),
            };
        }
        return { title: '', subtitle: '' };
    };

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onCloseModal}
            title={getTitleText().title}
            subtitle={
                <Typography variant="caption" color="textSecondary">
                    {getTitleText().subtitle}
                </Typography>
            }
            onPrimaryAction={
                (modal.step === 3 && modal.type === CHANNEL.WHATSAPP) ||
                (modal.step === 4 && modal.type === CHANNEL.WHATSAPP && modal.status === STATUS.NUMBER_EXISTS)
                    ? null
                    : onClickSubmit
            }
            primaryActionText={getActionsText().primaryText}
            primaryActionDisabled={modal.step === 2 && (isSubmitting || !isValid)}
            onSecondaryAction={
                modal.step === 4 && modal.type === CHANNEL.WHATSAPP && modal.status === STATUS.SUCCESS
                    ? null
                    : onCloseModal
            }
            secondaryActionText={getActionsText().secondaryText}
            secondaryActionDisabled={isSubmitting}
            onAdditionalAction={modal.step === 2 ? onClickBack : null}
            additionalTextButton={modal.step === 3 && modal.type === CHANNEL.LIVE_CHAT ? null : t('buttons.back')}
            additionalButtonType="outlined"
            additionalActionDisabled={isSubmitting}
        >
            {modal.step === 1 ? <SelectChannel modal={modal} setModal={setModal} /> : null}
            <FormProvider {...{ control, setValue, getValues, watch, trigger, twillioUid: twillioUid.current }}>
                {modal.step === 2 && modal.type === CHANNEL.SMS ? <SmsForm accountLocale={accountLocale} /> : null}
                {modal.step === 2 && modal.type === CHANNEL.WHATSAPP ? <WhatsAppForm /> : null}
                {modal.step === 2 && modal.type === CHANNEL.WHATSAPP_BUSINESS ? <WhatsAppBusinessForm /> : null}
                {modal.step === 2 && modal.type === CHANNEL.LIVE_CHAT ? <LiveChatForm /> : null}
                {modal.step === 2 && (modal.type === CHANNEL.FACEBOOK || modal.type === CHANNEL.INSTAGRAM) ? (
                    <FacebookForm type={modal.type} />
                ) : null}
                {modal.step === 2 && modal.type === CHANNEL.EMAIL ? <EmailForm /> : null}
            </FormProvider>
            {modal.step === 3 && modal.type === CHANNEL.LIVE_CHAT ? (
                <LiveChatCode liveChatPublicId={modal.liveChatPublicId} />
            ) : null}
            {modal.step === 3 && modal.type === CHANNEL.WHATSAPP ? <WhatsAppCheck qrCode={modal.qrCode} /> : null}
            {modal.step === 4 && modal.type === CHANNEL.WHATSAPP ? <WhatsAppStatus status={modal.status} /> : null}
        </Modal>
    );
};

export default CreateChannel;
